import type {
  TournamentCompetitionFormat,
  TournamentParticipationMode,
  TournamentScoringMode,
  TournamentTier,
  TournamentVisibility,
} from '../../types';

export type TournamentScale =
  | 'friend_group'
  | 'local_crew'
  | 'fishing_club'
  | 'boat_crew'
  | 'city'
  | 'province'
  | 'island'
  | 'country'
  | 'open_global';

export const TOURNAMENT_SCALE_OPTIONS: Array<{
  value: TournamentScale;
  label: string;
  hint: string;
  tier: TournamentTier;
  visibility: TournamentVisibility;
  participationMode?: TournamentParticipationMode;
  locationScope?: 'worldwide' | 'country' | 'region' | 'water' | 'custom';
}> = [
  { value: 'friend_group', label: 'Friend Group', hint: 'A small private event for people you know.', tier: 'crew', visibility: 'private' },
  { value: 'local_crew', label: 'Local Crew', hint: 'Your regular fishing crew or nearby circle.', tier: 'crew', visibility: 'crew' },
  { value: 'fishing_club', label: 'Fishing Club', hint: 'A club-scale event with room to grow.', tier: 'community', visibility: 'public' },
  { value: 'boat_crew', label: 'Boat Crew', hint: 'Built for anglers competing as a boat/team.', tier: 'crew', visibility: 'crew', participationMode: 'team' },
  { value: 'city', label: 'City', hint: 'Public tournament for a city or metro fishing community.', tier: 'community', visibility: 'public', locationScope: 'region' },
  { value: 'province', label: 'Province / State', hint: 'Regional public competition.', tier: 'community', visibility: 'public', locationScope: 'region' },
  { value: 'island', label: 'Island', hint: 'Island-wide fishing competition.', tier: 'community', visibility: 'public', locationScope: 'region' },
  { value: 'country', label: 'Country', hint: 'Country-wide public event.', tier: 'community', visibility: 'public', locationScope: 'country' },
  { value: 'open_global', label: 'Open Global', hint: 'Worldwide competition shown near each eligible angler on Big Blue.', tier: 'community', visibility: 'public', locationScope: 'worldwide' },
];

export const TOURNAMENT_GROUP_NAME_IDEAS = [
  'Local Legends',
  'Pier Rats',
  'Reef Goblins',
  'Tuna Mafia',
  'Kakap Klub',
  'Tenggiri Task Force',
  'Gabus Gang',
  'Toman Hunters',
  'GT Addicts',
  'Weekend Warriors',
  'Boat Boys',
  'No Fish No Problem',
] as const;

export type TournamentTemplate = {
  key: string;
  name: string;
  competitionFormat: TournamentCompetitionFormat;
  scoringMode: TournamentScoringMode;
  participationMode?: TournamentParticipationMode;
  eligibleSpecies?: string[];
  tournamentScale?: TournamentScale;
};

export const TOURNAMENT_TEMPLATES: TournamentTemplate[] = [
  { key: 'biggest_fish_wins', name: 'Biggest Fish Wins', competitionFormat: 'biggest_fish', scoringMode: 'longest' },
  { key: 'biggest_kakap', name: 'Biggest Kakap', competitionFormat: 'biggest_fish', scoringMode: 'longest', eligibleSpecies: ['Kakap'] },
  { key: 'biggest_gt', name: 'Biggest GT', competitionFormat: 'biggest_fish', scoringMode: 'longest', eligibleSpecies: ['Giant Trevally'] },
  { key: 'tenggiri_smackdown', name: 'Tenggiri Smackdown', competitionFormat: 'biggest_fish', scoringMode: 'longest', eligibleSpecies: ['Tenggiri'] },
  { key: 'gabus_showdown', name: 'Gabus Showdown', competitionFormat: 'biggest_fish', scoringMode: 'longest', eligibleSpecies: ['Gabus'] },
  { key: 'toman_rumble', name: 'Toman Rumble', competitionFormat: 'biggest_fish', scoringMode: 'longest', eligibleSpecies: ['Toman'] },
  { key: 'tuna_run', name: 'Tuna Run', competitionFormat: 'heaviest_fish', scoringMode: 'heaviest', eligibleSpecies: ['Tuna'] },
  { key: 'reef_rumble', name: 'Reef Rumble', competitionFormat: 'species_count', scoringMode: 'species_count' },
  { key: 'pier_rat_cup', name: 'Pier Rat Cup', competitionFormat: 'biggest_fish', scoringMode: 'longest' },
  { key: 'weekend_war', name: 'Weekend War', competitionFormat: 'biggest_fish', scoringMode: 'best_3' },
  { key: 'boat_vs_boat', name: 'Boat vs Boat', competitionFormat: 'total_length', scoringMode: 'team_total', participationMode: 'team', tournamentScale: 'boat_crew' },
  { key: 'crew_vs_crew', name: 'Crew vs Crew', competitionFormat: 'total_length', scoringMode: 'team_total', participationMode: 'team', tournamentScale: 'fishing_club' },
  { key: 'city_vs_city', name: 'City vs City', competitionFormat: 'total_length', scoringMode: 'team_total', participationMode: 'team', tournamentScale: 'city' },
  { key: 'island_vs_island', name: 'Island vs Island', competitionFormat: 'total_length', scoringMode: 'team_total', participationMode: 'team', tournamentScale: 'island' },
  { key: 'indonesia_open', name: 'Indonesia Open', competitionFormat: 'biggest_fish', scoringMode: 'longest', tournamentScale: 'country' },
];

export const TOURNAMENT_FORMAT_PRESETS: Array<{
  key: string;
  label: string;
  competitionFormat: TournamentCompetitionFormat;
  scoringMode: TournamentScoringMode;
  participationMode?: TournamentParticipationMode;
}> = [
  { key: 'biggest', label: 'Biggest Fish', competitionFormat: 'biggest_fish', scoringMode: 'longest' },
  { key: 'top3', label: 'Top 3 Fish', competitionFormat: 'biggest_fish', scoringMode: 'best_3' },
  { key: 'most_fish', label: 'Most Fish', competitionFormat: 'most_fish', scoringMode: 'most_catches' },
  { key: 'most_species', label: 'Most Species', competitionFormat: 'species_count', scoringMode: 'species_count' },
  { key: 'species_only', label: 'Species Only', competitionFormat: 'species_count', scoringMode: 'checklist' },
  { key: 'team_total', label: 'Team Total', competitionFormat: 'total_length', scoringMode: 'team_total', participationMode: 'team' },
  { key: 'boat_total', label: 'Boat Total', competitionFormat: 'total_length', scoringMode: 'team_total', participationMode: 'team' },
];



export type RoddoQuickTournamentTemplate = {
  key: 'biggest_fish' | 'biggest_bass' | 'most_catches' | 'species_hunt';
  label: string;
  hint: string;
  competitionFormat: TournamentCompetitionFormat;
  scoringMode: TournamentScoringMode;
  eligibleSpecies?: string[];
  catchLimit?: number;
};

export const RODDO_QUICK_TOURNAMENT_TEMPLATES: RoddoQuickTournamentTemplate[] = [
  {
    key: 'biggest_fish',
    label: 'Biggest Fish',
    hint: 'Simple longest-fish competition. Great default for fast community play.',
    competitionFormat: 'biggest_fish',
    scoringMode: 'longest',
    eligibleSpecies: [],
    catchLimit: 1,
  },
  {
    key: 'biggest_bass',
    label: 'Biggest Bass',
    hint: 'Longest bass wins. Species can still be refined before submission.',
    competitionFormat: 'biggest_fish',
    scoringMode: 'longest',
    eligibleSpecies: ['Bass'],
    catchLimit: 1,
  },
  {
    key: 'most_catches',
    label: 'Most Catches',
    hint: 'Encourages repeated fishing and logging throughout the event.',
    competitionFormat: 'most_fish',
    scoringMode: 'most_catches',
    eligibleSpecies: [],
    catchLimit: 100,
  },
  {
    key: 'species_hunt',
    label: 'Species Hunt',
    hint: 'Most different species logged wins.',
    competitionFormat: 'species_count',
    scoringMode: 'species_count',
    eligibleSpecies: [],
    catchLimit: 100,
  },
];

export const TOURNAMENT_DURATION_PRESETS = [
  { key: '24h', label: '24 Hour', durationMs: 24 * 60 * 60_000 },
  { key: 'weekend', label: 'Weekend', durationMs: 2 * 24 * 60 * 60_000 },
  { key: '7day', label: '7 Day', durationMs: 7 * 24 * 60 * 60_000 },
  { key: 'monthly', label: 'Monthly', durationMs: 30 * 24 * 60 * 60_000 },
] as const;
